/**
 * Phase 65 D-19: Read-only invoice status audit log modal.
 *
 * Fetches GET /invoices/:id/log via fetchInvoiceLog (Plan 65-05) and renders
 * rows DESC (backend already sorts) using INVOICE_STATUS pills.
 *
 * Each row: { changedAt, fromStatus → toStatus, changedBy.name|'Система', note? }.
 * `fromStatus` is null on the create row (first event); pill renders as «—».
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchInvoiceLog } from '../api/invoices.js';
import { queryKeys } from '../lib/queryKeys.js';
import { getInvoiceStatusMeta } from '../lib/invoiceStatus.js';

const modalStyles = `
  .invoice-log-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1100; }
  .invoice-log-modal { background: #fff; border-radius: 14px; padding: 28px; max-width: 560px; width: 90%; max-height: 85vh; overflow-y: auto; box-shadow: 0 20px 50px rgba(15,23,42,0.25); }
  .invoice-log-modal h3 { margin: 0 0 16px 0; font-size: 18px; }
  .invoice-log-modal__row { display: grid; grid-template-columns: auto 1fr; column-gap: 12px; padding: 10px 0; border-bottom: 1px dashed #e2e8f0; }
  .invoice-log-modal__row:last-of-type { border-bottom: none; }
  .invoice-log-modal__date { font-size: 12px; color: #64748b; white-space: nowrap; padding-top: 2px; }
  .invoice-log-modal__transition { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; font-size: 13px; }
  .invoice-log-modal__pill { display: inline-block; padding: 2px 10px; border-radius: 999px; color: #fff; font-size: 11px; font-weight: 600; }
  .invoice-log-modal__dash { color: #94a3b8; font-size: 12px; }
  .invoice-log-modal__by { font-size: 12px; color: #475569; }
  .invoice-log-modal__note { font-size: 12px; color: #334155; margin-top: 4px; line-height: 1.4; word-break: break-word; }
  .invoice-log-modal__empty { padding: 24px; text-align: center; color: #94a3b8; font-size: 14px; }
  .invoice-log-modal__error { color: #b91c1c; }
  .invoice-log-modal__close { display: block; margin: 18px 0 0 auto; padding: 8px 16px; border-radius: 10px; border: 1px solid #cbd5e1; background: #fff; cursor: pointer; font-size: 14px; }
`;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(iso || '');
  }
}

function StatusPill({ status }) {
  if (!status) {
    return <span className="invoice-log-modal__dash">—</span>;
  }
  const meta = getInvoiceStatusMeta(status);
  return (
    <span
      className="invoice-log-modal__pill"
      style={{ background: meta.color }}
    >
      {meta.label}
    </span>
  );
}

/**
 * @param {{ invoiceId: string|null|undefined, onClose: () => void }} props
 */
export default function InvoiceStatusLogModal({ invoiceId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.invoices.log(invoiceId),
    queryFn: () => fetchInvoiceLog(invoiceId),
    enabled: !!invoiceId,
  });

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const rows = Array.isArray(data) ? data : [];

  return (
    <>
      <style>{modalStyles}</style>
      <div className="invoice-log-modal-overlay" onClick={() => {}}>
        <div
          className="invoice-log-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <h3>История смен статуса</h3>

          {isLoading && (
            <div className="invoice-log-modal__empty">Загрузка…</div>
          )}
          {!isLoading && error && (
            <div className="invoice-log-modal__empty invoice-log-modal__error">
              Ошибка загрузки истории
            </div>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <div className="invoice-log-modal__empty">История пуста</div>
          )}

          {!isLoading &&
            !error &&
            rows.map((row) => {
              const by =
                row.changedBy?.name ||
                row.changedBy?.email ||
                'Система';
              return (
                <div key={row.id} className="invoice-log-modal__row">
                  <div className="invoice-log-modal__date">
                    {formatDate(row.changedAt)}
                  </div>
                  <div>
                    <div className="invoice-log-modal__transition">
                      <StatusPill status={row.fromStatus} />
                      <span className="invoice-log-modal__dash">→</span>
                      <StatusPill status={row.toStatus} />
                      <span className="invoice-log-modal__by">— {by}</span>
                    </div>
                    {row.note && (
                      <div className="invoice-log-modal__note">· {row.note}</div>
                    )}
                  </div>
                </div>
              );
            })}

          <button
            type="button"
            className="invoice-log-modal__close"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </>
  );
}
